import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { SubTabBar } from "@/components/SubTabBar";
import { groupByMainTab } from "@/application/navigation";
import { fetchCompanyProfile, saveCompanyProfile, type CompanyProfile } from "@/repositories/companyRepository";

const EMPTY: CompanyProfile = {
  companyId: "",
  tradingName: "",
  legalName: "",
  companyType: "",
  industry: "",
  incorporationCountry: "",
  jurisdiction: "",
  email: "",
  phone: "",
  website: "",
  status: "Active",
  currency: "",
  registeredAddress: "",
  operatingAddress: "",
  businessNumber: "",
  foundingDate: "",
  incorporationDate: "",
  fiscalYearEnd: "",
  legalStatus: "Active",
  registrationStatus: "",
  notes: "",
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="form-field">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

export function AdminCompanyGeneralPage() {
  const [form, setForm] = useState<CompanyProfile>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const siblingScreens = groupByMainTab("Admin").find((g) => g.mainTab === "Company")?.screens ?? [];

  useEffect(() => {
    fetchCompanyProfile()
      .then((data) => setForm(data))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  function update<K extends keyof CompanyProfile>(key: K, value: CompanyProfile[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaveMessage(null);
    try {
      await saveCompanyProfile(form);
      setSaveMessage("Changes saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppShell portal="Admin" userName="Admin" userRole="Admin">
      <div className="breadcrumb">Admin / Company / General</div>
      <SubTabBar screens={siblingScreens} />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26 }}>Company</h1>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn-secondary" disabled title="Coming soon">
            Export Profile
          </button>
          <button className="btn-secondary" disabled title="Coming soon">
            Download PDF
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {loading && <div className="loading-state">Loading company profile…</div>}
      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {saveMessage && <p style={{ color: "#16a34a" }}>{saveMessage}</p>}

      {!loading && (
        <>
          <div className="dashboard-grid">
            <div className="card">
              <h2 className="card-title">Company Identity</h2>
              <div className="form-grid">
                <Field label="Legal Company Name">
                  <input
                    className="form-input"
                    value={form.legalName}
                    onChange={(e) => update("legalName", e.target.value)}
                    placeholder="e.g. AlifBee Education Technology Ltd."
                  />
                </Field>
                <Field label="Trading Name">
                  <input
                    className="form-input"
                    value={form.tradingName}
                    onChange={(e) => update("tradingName", e.target.value)}
                    placeholder="e.g. AlifBee"
                  />
                </Field>
                <Field label="Company Type">
                  <input
                    className="form-input"
                    value={form.companyType}
                    onChange={(e) => update("companyType", e.target.value)}
                    placeholder="e.g. Corporation"
                  />
                </Field>
                <Field label="Industry">
                  <input
                    className="form-input"
                    value={form.industry}
                    onChange={(e) => update("industry", e.target.value)}
                    placeholder="e.g. Education Technology"
                  />
                </Field>
                <Field label="Country of Incorporation">
                  <input
                    className="form-input"
                    value={form.incorporationCountry}
                    onChange={(e) => update("incorporationCountry", e.target.value)}
                    placeholder="e.g. Canada"
                  />
                </Field>
                <Field label="Jurisdiction">
                  <input
                    className="form-input"
                    value={form.jurisdiction}
                    onChange={(e) => update("jurisdiction", e.target.value)}
                    placeholder="e.g. Québec"
                  />
                </Field>
              </div>
            </div>

            <div className="card">
              <h2 className="card-title">Contact Details</h2>
              <div className="form-grid">
                <Field label="Company Email">
                  <input
                    className="form-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="e.g. portal@alifbee.com"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    className="form-input"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="e.g. +1 555 000 0000"
                  />
                </Field>
                <Field label="Website">
                  <input
                    className="form-input"
                    value={form.website}
                    onChange={(e) => update("website", e.target.value)}
                    placeholder="e.g. www.alifbee.com"
                  />
                </Field>
                <Field label="Company Status">
                  <select
                    className="form-input"
                    value={form.status}
                    onChange={(e) => update("status", e.target.value)}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </Field>
                <Field label="Currency">
                  <input
                    className="form-input"
                    value={form.currency}
                    onChange={(e) => update("currency", e.target.value)}
                    placeholder="e.g. USD"
                  />
                </Field>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 20 }}>
            <h2 className="card-title">Company Addresses</h2>
            <div className="form-grid">
              <Field label="Registered Address">
                <textarea
                  className="form-input form-textarea"
                  value={form.registeredAddress}
                  onChange={(e) => update("registeredAddress", e.target.value)}
                  placeholder="Legal registered address"
                />
              </Field>
              <Field label="Operating Address">
                <textarea
                  className="form-input form-textarea"
                  value={form.operatingAddress}
                  onChange={(e) => update("operatingAddress", e.target.value)}
                  placeholder="Day-to-day operating address"
                />
              </Field>
            </div>
            <div style={{ marginTop: 14 }}>
              <Field label="Business Number">
                <input
                  className="form-input"
                  style={{ maxWidth: 260 }}
                  value={form.businessNumber}
                  onChange={(e) => update("businessNumber", e.target.value)}
                  placeholder="e.g. 1175879981"
                />
              </Field>
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}
